/**
 * Starter templates.
 *
 * Each template is plain board-core nodes laid out relative to (0, 0) — the
 * same factories the tools use, just called up front instead of from a
 * gesture. Inserting one is nothing more than an import: translate every node
 * by the drop point, hand fresh z-order keys to `doc.addMany`, then let
 * `reparentIntoFrames` nest everything into the frame by geometry, exactly as
 * a hand-drawn sticky would be.
 */
import {
  DEFAULT_TEXT_STYLE,
  createFrame,
  createShape,
  createSticky,
  createText,
  type BoardNode,
  type NodeId,
  type UserId,
} from '@kanvas/board-core';

export interface BoardTemplate {
  id: string;
  name: string;
  description: string;
  size: { w: number; h: number };
  accent: string;
  build: (userId: UserId, nextIndex: () => string) => BoardNode[];
}

/** Shifts every positioned node by (dx, dy); templates are authored at (0,0). */
export function translateNodes(nodes: readonly BoardNode[], dx: number, dy: number): BoardNode[] {
  return nodes.map((n) => ({ ...n, x: n.x + dx, y: n.y + dy }));
}

function heading(
  text: string,
  x: number,
  y: number,
  w: number,
  userId: UserId,
  index: string,
  overrides: { fontSize?: number; color?: string; align?: 'left' | 'center' } = {},
): BoardNode {
  return createText(x, y, { userId, index }, {
    w,
    h: 36,
    text,
    autoHeight: false,
    textStyle: {
      ...DEFAULT_TEXT_STYLE,
      bold: true,
      fontSize: overrides.fontSize ?? 20,
      align: overrides.align ?? 'left',
      valign: 'top',
      color: overrides.color ?? '#1A1F28',
    },
  });
}

function note(
  text: string,
  x: number,
  y: number,
  fill: string,
  userId: UserId,
  index: string,
  size = 180,
): BoardNode {
  return createSticky(x, y, { userId, index }, { w: size, h: size, text, fill });
}

function divider(x: number, y: number, w: number, h: number, userId: UserId, index: string): BoardNode {
  return createShape('rect', x, y, { userId, index }, {
    w,
    h,
    style: {
      fill: '#D8DCE3',
      fillStyle: 'solid',
      stroke: 'transparent',
      strokeWidth: 0,
      strokeStyle: 'solid',
      radius: 0,
      shadow: false,
    },
  });
}

function panel(
  x: number,
  y: number,
  w: number,
  h: number,
  fill: string,
  userId: UserId,
  index: string,
): BoardNode {
  return createShape('rect', x, y, { userId, index }, {
    w,
    h,
    style: {
      fill,
      fillStyle: 'solid',
      stroke: 'transparent',
      strokeWidth: 0,
      strokeStyle: 'solid',
      radius: 12,
      shadow: false,
    },
  });
}

const FRAME_W = 1500;
const FRAME_H = 900;

const retro: BoardTemplate = {
  id: 'retro',
  name: 'Ретроспектива',
  description: 'Что сработало · что не сработало · что улучшить',
  size: { w: FRAME_W, h: FRAME_H },
  accent: '#3B82F6',
  build: (userId, nextIndex) => {
    const out: BoardNode[] = [];
    out.push(createFrame(0, 0, { userId, index: nextIndex() }, { w: FRAME_W, h: FRAME_H, name: 'Ретроспектива' }));

    const cols = [
      { x: 40, title: '😀 Что сработало', fill: '#CFF7D3', hint: 'Например: релиз прошёл без инцидентов' },
      { x: 40 + (FRAME_W - 80) / 3, title: '😕 Что не сработало', fill: '#FFE1C7', hint: 'Например: поздно узнали о блокере' },
      { x: 40 + 2 * ((FRAME_W - 80) / 3), title: '💡 Что улучшить', fill: '#D6E4FF', hint: 'Например: чаще синхронизироваться' },
    ];
    const colW = (FRAME_W - 80) / 3 - 20;

    for (const col of cols) {
      out.push(heading(col.title, col.x, 30, colW, userId, nextIndex()));
      out.push(note(col.hint, col.x, 84, col.fill, userId, nextIndex(), Math.min(colW, 200)));
    }
    out.push(divider(40 + (FRAME_W - 80) / 3 - 20, 24, 2, FRAME_H - 48, userId, nextIndex()));
    out.push(divider(40 + 2 * ((FRAME_W - 80) / 3) - 20, 24, 2, FRAME_H - 48, userId, nextIndex()));
    return out;
  },
};

const kanban: BoardTemplate = {
  id: 'kanban',
  name: 'Канбан',
  description: 'Backlog · To Do · In Progress · Done',
  size: { w: FRAME_W, h: FRAME_H },
  accent: '#8B5CF6',
  build: (userId, nextIndex) => {
    const out: BoardNode[] = [];
    out.push(createFrame(0, 0, { userId, index: nextIndex() }, { w: FRAME_W, h: FRAME_H, name: 'Канбан' }));

    const titles = ['Backlog', 'To Do', 'In Progress', 'Done'];
    const colW = (FRAME_W - 80) / 4 - 16;
    const sample: Record<string, string[]> = {
      'To Do': ['Задача 1'],
      'In Progress': ['Задача 2'],
      Done: ['Задача 0 ✔'],
    };

    titles.forEach((title, i) => {
      const x = 40 + i * ((FRAME_W - 80) / 4);
      out.push(heading(title, x, 30, colW, userId, nextIndex(), { align: 'center' }));
      if (i > 0) out.push(divider(x - 20, 24, 2, FRAME_H - 48, userId, nextIndex()));
      const cards = sample[title] ?? [];
      cards.forEach((cardText, j) => {
        out.push(note(cardText, x, 84 + j * 96, '#FFF3B0', userId, nextIndex(), Math.min(colW, 168)));
      });
    });
    return out;
  },
};

const swot: BoardTemplate = {
  id: 'swot',
  name: 'SWOT-анализ',
  description: 'Сильные и слабые стороны, возможности, угрозы',
  size: { w: 1400, h: 1000 },
  accent: '#10B981',
  build: (userId, nextIndex) => {
    const out: BoardNode[] = [];
    const w = 1400;
    const h = 1000;
    out.push(createFrame(0, 0, { userId, index: nextIndex() }, { w, h, name: 'SWOT-анализ' }));

    const gap = 16;
    const qw = (w - gap * 3) / 2;
    const qh = (h - gap * 3) / 2;
    const quadrants = [
      { x: gap, y: gap, fill: '#DAF5E1', title: 'Strengths · Сильные стороны' },
      { x: gap * 2 + qw, y: gap, fill: '#FDE2E2', title: 'Weaknesses · Слабые стороны' },
      { x: gap, y: gap * 2 + qh, fill: '#DCEBFF', title: 'Opportunities · Возможности' },
      { x: gap * 2 + qw, y: gap * 2 + qh, fill: '#FDECC8', title: 'Threats · Угрозы' },
    ];
    for (const q of quadrants) {
      out.push(panel(q.x, q.y, qw, qh, q.fill, userId, nextIndex()));
      out.push(heading(q.title, q.x + 20, q.y + 16, qw - 40, userId, nextIndex(), { fontSize: 18 }));
    }
    return out;
  },
};

const brainstorm: BoardTemplate = {
  id: 'brainstorm',
  name: 'Мозговой штурм',
  description: 'Тема в центре, идеи — по одной на стикер',
  size: { w: 1400, h: 1000 },
  accent: '#F59E0B',
  build: (userId, nextIndex) => {
    const out: BoardNode[] = [];
    const w = 1400;
    const h = 1000;
    out.push(createFrame(0, 0, { userId, index: nextIndex() }, { w, h, name: 'Мозговой штурм' }));
    out.push(heading('Тема мозгового штурма', 60, 32, w - 120, userId, nextIndex(), { fontSize: 26, align: 'center' }));
    out.push(
      createText(60, 74, { userId, index: nextIndex() }, {
        w: w - 120,
        h: 28,
        text: '1 идея = 1 стикер. Никакой критики на этом этапе — оценим позже.',
        autoHeight: false,
        textStyle: { ...DEFAULT_TEXT_STYLE, fontSize: 14, italic: true, align: 'center', valign: 'top', color: '#6B7280' },
      }),
    );

    const colors = ['#FFF3B0', '#CFF7D3', '#FFD6E7', '#D6E4FF', '#FFE1C7', '#E6D6FF'];
    const cols = 3;
    const startX = 100;
    const startY = 160;
    const gapX = (w - 200) / cols;
    for (let i = 0; i < 6; i++) {
      const cx = startX + (i % cols) * gapX;
      const cy = startY + Math.floor(i / cols) * 220;
      out.push(note('', cx, cy, colors[i % colors.length]!, userId, nextIndex(), 190));
    }
    return out;
  },
};

export const BOARD_TEMPLATES: BoardTemplate[] = [retro, kanban, swot, brainstorm];

export function findTemplate(id: string): BoardTemplate | undefined {
  return BOARD_TEMPLATES.find((t) => t.id === id);
}
